/**
 * The application shell.
 *
 * Sidebar navigation rather than one scrolling column. The first build stacked
 * clients, provisioning and the fleet on a single page, which put an occasional
 * action permanently above the thing an operator actually looks at.
 */

import { type ReactNode } from 'react';

import { routeHref, type RouteName } from './routing.ts';

/* Inline SVG rather than an icon package: four icons do not justify a
   dependency, and these never need to change. */
const ICONS: Record<RouteName, ReactNode> = {
  overview: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  ),
  clients: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M16 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20" />
      <circle cx="9" cy="7" r="3.2" />
      <path d="M22 20v-1.5a4 4 0 0 0-3-3.87M16.5 4.2a3.2 3.2 0 0 1 0 5.9" />
    </svg>
  ),
  devices: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="2.5" y="4" width="19" height="7" rx="2" />
      <rect x="2.5" y="13" width="19" height="7" rx="2" />
      <path d="M6.5 7.5h.01M6.5 16.5h.01" strokeLinecap="round" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5 20v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1" />
    </svg>
  ),
  audit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M6 3h9l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v5h5M8.5 13h7M8.5 17h4" strokeLinecap="round" />
    </svg>
  ),
  /* Never rendered in the sidebar — the accept page has no shell — but the map
     is keyed by RouteName, so omitting it would be a type error rather than a
     silent gap. */
  accept: null,
};

/**
 * Sidebar entries, in order.
 *
 * Keyed by RouteName minus the routes with no shell, so a new page is a
 * deliberate addition here rather than something that appears by existing.
 */
const LABELS: Partial<Record<RouteName, string>> = {
  overview: 'Overview',
  clients: 'Clients',
  devices: 'Devices',
  users: 'People',
  audit: 'Audit log',
};

export interface ShellProps {
  active: RouteName;
  counts: Partial<Record<RouteName, number>>;
  title: string;
  subtitle?: string | undefined;
  actions?: ReactNode;
  onSignOut: () => void;
  children: ReactNode;
}

export function Shell({
  active,
  counts,
  title,
  subtitle,
  actions,
  onSignOut,
  children,
}: ShellProps): React.JSX.Element {
  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <span className="brand__mark">JE</span>
          JE Protect
        </div>

        <nav className="nav">
          {(Object.keys(LABELS) as RouteName[]).map((name) => (
            <a
              key={name}
              className={name === active ? 'nav__item nav__item--active' : 'nav__item'}
              href={routeHref(name)}
              aria-current={name === active ? 'page' : undefined}
            >
              {ICONS[name]}
              {LABELS[name]}
              {counts[name] === undefined ? null : <span className="nav__count">{counts[name]}</span>}
            </a>
          ))}
        </nav>

        <div className="sidebar__foot">
          <button type="button" className="btn btn--sm" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="page-head">
          <div className="page-head__title">
            <h1>{title}</h1>
            {subtitle ? <p className="page-head__sub">{subtitle}</p> : null}
          </div>
          {actions ? <div className="page-head__actions">{actions}</div> : null}
        </header>

        <div className="page-body">{children}</div>
      </div>
    </div>
  );
}
